import React, { ReactNode } from 'react';
import { Box, Collapse, Grid2 as Grid, Stack, SxProps, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useDashboardContext } from 'framework/ui/dashboard';
import { AccountWithProjects } from 'domain/shared/newProject';
import { LinkField, TextField } from 'framework/ui/field';
import { ALetterIcon, Tooltip } from 'framework/ui/mui-builder';
import { EmployeeLinkFieldWithNameLoading, useIsEmployeeProfileAccessible } from 'domain/shared/employee';

interface DashboardItemProps {
  itemId: string;
  account: AccountWithProjects;
  expandedComponent: ReactNode;
  sx?: SxProps;
}

const ProjectDashboardAccountItem: React.FC<DashboardItemProps> = ({ itemId, account, expandedComponent, sx }) => {
  const { expandedItems, toggleExpand } = useDashboardContext();

  const isExpanded = expandedItems.has(itemId);
  const accountManager = account.projects?.[0]?.accountData?.ownerAlias;
  const isAccountManagerAccessible = useIsEmployeeProfileAccessible(accountManager);

  const projectCount = account.projects?.length || 0;
  const projectText = projectCount === 1 ? '1 active project' : `${projectCount} active projects`;

  return (
    <Stack direction="column" className="dashboard-item-container" sx={{ padding: '0px!important', ...sx }}>
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{ minHeight: '40px', padding: '2px 4px' }}
      >
        <Box>
          <IconButton
            size="small"
            onClick={() => toggleExpand(itemId)}
            className={`dashboard-item-toggle ${isExpanded ? 'expanded' : ''}`}
          >
            <KeyboardArrowDownIcon sx={{ width: '21px' }} />
          </IconButton>
        </Box>
        <Grid container size={12} sx={{ width: '100%', alignItems: 'center' }}>
          <Grid size={4.5}>
            <LinkField
              isInternal
              linkText={`${account.accountName} - ${account.accountId}`}
              value={`/crm/accounts/${account.accountId}`}
            />
          </Grid>
          <Grid size={2} display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Tooltip title="Account manager">
              <span style={{ alignItems: 'center', display: 'flex' }}>
                <ALetterIcon sx={{ fontSize: '1rem' }} />
              </span>
            </Tooltip>
            <TextField
              value={
                <EmployeeLinkFieldWithNameLoading
                  ignoredNameParts={['middleName', 'maidenName']}
                  value={accountManager}
                  isLink={isAccountManagerAccessible}
                  isOpenInNewTab
                />
              }
              label=""
            />
          </Grid>
          <Grid size={5}>
            <Typography color="textSecondary">{projectText}</Typography>
          </Grid>
        </Grid>
      </Stack>
      <Collapse in={isExpanded} timeout={300} unmountOnExit>
        {expandedComponent}
      </Collapse>
    </Stack>
  );
};

export default ProjectDashboardAccountItem;
